import { useEffect, useRef } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { messagingPaths } from "../api/services.ts"
import {
  accessRevoked,
  liveMessageArrived,
  messageChanged,
  readMarkerMoved,
  setConnected,
  setUnreadCount,
  setUnreadCounts,
  setUnreadTotal
} from "../redux/messaging.slice.ts"
import {
  catchUpMessages,
  fetchConversations
} from "../redux/messaging.thunks.ts"
import type { Message } from "../types/messaging.types.ts"
import { CONVERSATION_EVENT, WS_EVENT } from "../types/messaging.types.ts"

// Under the ALB's 60s idle timeout, matching the notification socket.
const HEARTBEAT_MS = 25000
const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30000

interface Options {
  portal?: boolean
}

/**
 * One socket for every conversation the user can reach.
 *
 * On reconnect the hook asks each held conversation for what it missed rather
 * than refetching a page — the gap is exactly the messages after the newest one
 * this client holds, so nothing arrives twice and nothing is silently skipped.
 */
export const useMessagingSocket = ({ portal = false }: Options = {}) => {
  const dispatch = useAppDispatch()
  const accessToken = useAppSelector((state) => state.auth.accessToken)
  const openConversationIds = useAppSelector(
    (state) => state.messaging.openConversationIds
  )
  // Subscribed is not the same as looked at. The conversation list
  // subscribes to every room so its badges stay live; only one is on screen,
  // and only that one should suppress its own notifications.
  const activeConversationId = useAppSelector(
    (state) => state.messaging.activeConversationId
  )
  const activeRef = useRef<string | null>(null)
  activeRef.current = activeConversationId

  const currentUserId = useAppSelector((state) => state.auth.userInfo?.id)
  const meRef = useRef<string | undefined>(currentUserId)
  meRef.current = currentUserId

  const messagesByConversation = useAppSelector(
    (state) => state.messaging.messagesByConversation
  )

  const socketRef = useRef<WebSocket | null>(null)
  const openRef = useRef<string[]>(openConversationIds)
  const historyRef = useRef(messagesByConversation)
  const hasConnectedBefore = useRef(false)

  openRef.current = openConversationIds
  historyRef.current = messagesByConversation

  useEffect(() => {
    if (!accessToken) return

    let heartbeat: ReturnType<typeof setInterval> | undefined
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined
    let reconnectAttempts = 0
    let closedByCleanup = false

    // Every history held, not just the rooms on screen: a cached room shown
    // again later is not refetched, so a gap left here would stay.
    const catchUp = () => {
      Object.entries(historyRef.current).forEach(([conversationId, rows]) => {
        const newest = rows[rows.length - 1]
        if (newest) {
          dispatch(
            catchUpMessages({
              conversationId,
              after: newest.created,
              portal
            })
          )
        }
      })
      // Membership may also have moved while the socket was down, and only a
      // refetch can say which conversations are still the user's.
      dispatch(fetchConversations({ portal }))
    }

    const connect = () => {
      const socket = new WebSocket(messagingPaths(portal).socket(accessToken))
      socketRef.current = socket

      socket.onopen = () => {
        dispatch(setConnected(true))
        reconnectAttempts = 0
        if (hasConnectedBefore.current) catchUp()
        hasConnectedBefore.current = true

        if (openRef.current.length) {
          socket.send(
            JSON.stringify({
              type: "subscribe",
              conversation_ids: openRef.current,
              viewing: activeRef.current
            })
          )
        }

        heartbeat = setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "ping" }))
          }
        }, HEARTBEAT_MS)
      }

      socket.onmessage = (event) => {
        let frame: Record<string, unknown>
        try {
          frame = JSON.parse(event.data)
        } catch {
          return
        }

        if (frame.type === WS_EVENT.INITIAL_STATE) {
          dispatch(setUnreadTotal(Number(frame.unread_total ?? 0)))
          dispatch(
            setUnreadCounts(
              (frame.unread_by_conversation ?? {}) as Record<string, number>
            )
          )
          return
        }

        if (frame.type === WS_EVENT.READ_STATE) {
          dispatch(
            setUnreadCount({
              conversationId: String(frame.conversation_id),
              count: Number(frame.unread_count ?? 0)
            })
          )
          return
        }

        if (frame.type === WS_EVENT.SUBSCRIBED) {
          const refused = (frame.refused ?? []) as string[]
          refused.forEach((conversationId) =>
            dispatch(accessRevoked(String(conversationId)))
          )
          return
        }

        if (frame.type !== WS_EVENT.CONVERSATION_EVENT) return

        switch (frame.event) {
          case CONVERSATION_EVENT.MESSAGE_CREATED: {
            const message = frame.message as Message
            // Arrival is what the badge counts, wherever the reader happens to
            // be — the server relays into this socket for every conversation
            // they can read, not only the ones a screen has subscribed to.
            // Your own message is never unread to you, which is the same rule
            // the server applies when it recounts.
            //
            // A message in the conversation on screen is counted here too and
            // cleared a moment later by the read that screen reports, so a
            // message arriving in a background tab stays counted rather than
            // being cleared by a tab nobody is looking at.
            dispatch(
              liveMessageArrived({
                message,
                countsAsUnread: message.sender?.id !== meRef.current
              })
            )
            break
          }
          case CONVERSATION_EVENT.MESSAGE_UPDATED:
          case CONVERSATION_EVENT.MESSAGE_DELETED:
            dispatch(messageChanged(frame.message as never))
            break
          case CONVERSATION_EVENT.READ:
            if (frame.last_read_at) {
              dispatch(
                readMarkerMoved({
                  conversationId: String(frame.conversation_id),
                  lastReadAt: String(frame.last_read_at),
                  currentUserId: meRef.current
                })
              )
            }
            break
          case CONVERSATION_EVENT.ACCESS_REVOKED:
            dispatch(accessRevoked(String(frame.conversation_id)))
            break
          case CONVERSATION_EVENT.MEMBERSHIP:
            // The roster moved; which conversations are still reachable is a
            // server question, so ask rather than patch the list locally.
            dispatch(fetchConversations({ portal }))
            break
          default:
            break
        }
      }

      socket.onclose = () => {
        dispatch(setConnected(false))
        if (heartbeat) clearInterval(heartbeat)
        if (closedByCleanup) return
        const delay = Math.min(
          RECONNECT_BASE_MS * 2 ** reconnectAttempts,
          RECONNECT_MAX_MS
        )
        reconnectAttempts += 1
        reconnectTimer = setTimeout(connect, delay)
      }

      socket.onerror = () => socket.close()
    }

    connect()

    return () => {
      closedByCleanup = true
      if (heartbeat) clearInterval(heartbeat)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      socketRef.current?.close()
      socketRef.current = null
    }
  }, [accessToken, portal, dispatch])

  // Re-declaring what is open is cheap and idempotent, so it rides on the
  // list changing rather than on a reconnect alone.
  const openKey = openConversationIds.join(",")
  useEffect(() => {
    const socket = socketRef.current
    if (socket?.readyState !== WebSocket.OPEN) return
    socket.send(
      JSON.stringify({
        type: "subscribe",
        conversation_ids: openKey ? openKey.split(",") : [],
        viewing: activeConversationId
      })
    )
  }, [openKey, activeConversationId])

  return socketRef
}
